import React, { useState, useRef, useEffect } from "react";
import { Languages, ChevronDown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { translateDoc } from "@/lib/rexxponsApi";

const LANGS = [
  "English", "Italian", "Spanish", "French", "German", "Portuguese",
  "Dutch", "Arabic", "Hindi", "Chinese", "Japanese", "Korean", "Russian",
];

export const TranslateControl = ({ docId, docKind, original, onTranslated }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState("Original");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = async (lang) => {
    setOpen(false);
    if (lang === current) return;
    if (lang === "Original") {
      if (original) onTranslated(original);
      setCurrent("Original");
      return;
    }
    setBusy(true);
    const tid = toast.loading(`Translating into ${lang}…`);
    try {
      const doc = await translateDoc(docId, docKind, lang);
      onTranslated(doc);
      setCurrent(lang);
      toast.success(`Translated into ${lang}.`, { id: tid });
    } catch (e) {
      toast.error(e?.message || "Translation failed. Please try again.", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="translate-btn"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-primary transition-colors border border-border hover:border-primary/40 rounded-full px-3 py-1.5 disabled:opacity-60"
      >
        {busy ? <Loader2 strokeWidth={1.8} className="w-3.5 h-3.5 animate-spin" /> : <Languages strokeWidth={1.5} className="w-3.5 h-3.5" />}
        {busy ? "Translating…" : `Translate · ${current}`}
        <ChevronDown strokeWidth={1.8} className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="translate-menu"
          className="absolute z-50 mt-2 left-0 w-48 max-h-72 overflow-y-auto bg-card border border-border rounded-xl shadow-2xl p-1.5"
        >
          {original && (
            <button
              data-testid="translate-option-original"
              onClick={() => choose("Original")}
              className="w-full flex items-center justify-between text-left text-sm px-3 py-2 rounded-lg text-foreground hover:bg-secondary transition-colors"
            >
              Original {current === "Original" && <Check className="w-3.5 h-3.5 text-primary" />}
            </button>
          )}
          {LANGS.map((l) => (
            <button
              key={l}
              data-testid={`translate-option-${l.toLowerCase()}`}
              onClick={() => choose(l)}
              className="w-full flex items-center justify-between text-left text-sm px-3 py-2 rounded-lg text-foreground hover:bg-secondary transition-colors"
            >
              {l} {current === l && <Check className="w-3.5 h-3.5 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
